/**
 * SuperAdmin Audit page — cross-group event feed.
 *
 * Reads from publisher-groups-audit via GET /admin/super/audit.
 * Filters: actor (free text, Cognito sub), event_type (dropdown), limit.
 * Cursor pagination — "Load more" button appends the next page.
 * CSV export of currently-loaded rows.
 *
 * Guarded by SuperAdmins Cognito group.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Download, FileText, Home, RefreshCw } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { useToast } from '../components/common/ToastContainer';
import { useAuth } from '../context/AuthContext';
import publisherGroupsService from '../services/publisherGroupsService';
import type { AuditEvent } from '../types/publisherGroups';

const EVENT_TYPES = [
  '',
  'GROUP_CREATED',
  'GROUP_UPDATED',
  'GROUP_ARCHIVED',
  'MEMBER_ADDED',
  'MEMBER_REMOVED',
  'LISTING_PUBLISHED',
  'LISTING_UPDATED',
  'LISTING_UNPUBLISHED',
  'SUBSCRIBE_ALLOWED',
  'SUBSCRIBE_DENIED',
  'MANAGER_ASSIGNED',
  'MANAGER_REVOKED',
  'MIGRATION_COMPLETED',
];

function formatIST(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return '—';
  }
}

function downloadCsv(filename: string, rows: AuditEvent[]) {
  const header = ['timestamp', 'audit_partition', 'event_type', 'actor', 'role', 'target', 'metadata'];
  const escape = (v: any): string => {
    let s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
    // CSV-injection guard: cells beginning with =/+/-/@ are interpreted as
    // formulas by Excel / Sheets / LibreOffice. Prefix with a single quote
    // so spreadsheet apps render the value as text rather than executing it.
    if (s && /^[=+\-@]/.test(s)) s = `'${s}`;
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    header.join(','),
    ...rows.map((r) => [
      r.created_at,
      r.audit_partition,
      r.event_type,
      r.actor_user_id,
      r.actor_role,
      r.target_id ?? '',
      r.metadata ?? {},
    ].map(escape).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const SuperAdminAuditPage: React.FC = () => {
  const { isSuperAdmin, isLoading: authLoading } = useAuth();
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => { toastRef.current = toast; }, [toast]);

  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state — applied on Apply button to avoid re-fetching on every keystroke
  const [actorFilter, setActorFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<{ actor?: string; eventType?: string }>({});

  const fetchPage = useCallback(async (opts: {
    cursor?: string;
    append: boolean;
    actor?: string;
    eventType?: string;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const page = await publisherGroupsService.globalAudit({
        cursor: opts.cursor,
        limit: 50,
        actor: opts.actor || undefined,
        eventType: opts.eventType || undefined,
      });
      setRows((prev) => (opts.append ? [...prev, ...page.data] : page.data));
      setCursor(page.next_cursor ?? null);
      setHasMore(Boolean(page.next_cursor));
    } catch (err: any) {
      setError(err.message || 'Failed to load audit events');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load + reload when applied filters change. Skip entirely until
  // auth has loaded AND caller is SuperAdmin — otherwise a brief auth-loading
  // window would 403 the request and surface as a fetch error on first paint.
  useEffect(() => {
    if (authLoading || !isSuperAdmin) return;
    fetchPage({
      append: false,
      actor: appliedFilters.actor,
      eventType: appliedFilters.eventType,
    });
  }, [appliedFilters, fetchPage, authLoading, isSuperAdmin]);

  const applyFilters = () => {
    setAppliedFilters({
      actor: actorFilter.trim() || undefined,
      eventType: eventTypeFilter || undefined,
    });
  };

  const clearFilters = () => {
    setActorFilter('');
    setEventTypeFilter('');
    setAppliedFilters({});
  };

  const loadMore = () => {
    if (!cursor || isLoading) return;
    fetchPage({
      cursor,
      append: true,
      actor: appliedFilters.actor,
      eventType: appliedFilters.eventType,
    });
  };

  const exportCsv = () => {
    if (rows.length === 0) {
      toast.showWarning('No rows to export');
      return;
    }
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadCsv(`publisher-groups-audit-${ts}.csv`, rows);
    toast.showSuccess(`Exported ${rows.length} rows`);
  };

  // Auth gate: redirect non-SuperAdmins BEFORE any page chrome renders. Hooks
  // above always run (so order is stable); fetches are already gated above.
  // While auth is loading we render nothing — avoids a chrome flash that
  // resolves to a redirect milliseconds later.
  if (authLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  return (
    <StandardLayout>
      <PageHeader
        title="Audit Log"
        pageType="management"
        icon={<FileText className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Platform Owner' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchPage({ append: false, ...appliedFilters })}
              disabled={isLoading}
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              leftIcon={<Download className="h-4 w-4" />}
              disabled={rows.length === 0}
            >
              Export CSV
            </Button>
          </div>
        }
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                Actor (Cognito sub)
              </label>
              <Input
                value={actorFilter}
                onChange={(e) => setActorFilter(e.target.value)}
                placeholder="Paste a user_id to filter…"
              />
            </div>
            <div className="min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                Event Type
              </label>
              <select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md px-3 py-2 text-sm"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t || 'all'} value={t}>
                    {t || 'All events'}
                  </option>
                ))}
              </select>
            </div>
            <Button onClick={applyFilters} disabled={isLoading}>
              Apply
            </Button>
            <Button variant="ghost" onClick={clearFilters} disabled={isLoading}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Events ({rows.length}{hasMore ? '+' : ''})</CardTitle>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {appliedFilters.actor && <>actor: <code>{appliedFilters.actor}</code>{' · '}</>}
              {appliedFilters.eventType && <>type: <code>{appliedFilters.eventType}</code></>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && rows.length === 0 ? (
            <LoadingSpinner />
          ) : rows.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No audit events match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Timestamp (IST)
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Event
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Partition
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Actor
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Target
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Metadata
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.map((r, idx) => (
                    <tr
                      key={r.event_id || `${r.sk}#${idx}`}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300 whitespace-nowrap font-mono text-xs">
                        {formatIST(r.created_at)}
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">
                        {r.event_type}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                        <code className="text-xs">{r.audit_partition}</code>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                        <div className="text-xs">
                          <code>{r.actor_user_id}</code>
                          {r.actor_role && <div className="text-gray-400">{r.actor_role}</div>}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                        {r.target_id ? <code className="text-xs">{r.target_id}</code> : '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300 max-w-xs">
                        {r.metadata && Object.keys(r.metadata).length > 0 ? (
                          <code className="text-xs break-words">{JSON.stringify(r.metadata)}</code>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination footer */}
          {(hasMore || isLoading) && (
            <div className="flex justify-center border-t border-gray-200 dark:border-gray-700 px-4 py-3">
              <Button
                variant="outline"
                size="sm"
                onClick={loadMore}
                disabled={!hasMore || isLoading}
              >
                {isLoading ? 'Loading…' : hasMore ? 'Load more' : 'No more results'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </StandardLayout>
  );
};

export default SuperAdminAuditPage;
